import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { preventiviAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  FileText,
  Eye,
  Trash2,
  Filter,
} from 'lucide-react';

const Preventivi = () => {
  const [preventivi, setPreventivi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statoFilter, setStatoFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPreventivo, setSelectedPreventivo] = useState(null);

  useEffect(() => {
    fetchPreventivi();
  }, [statoFilter]);

  const fetchPreventivi = async () => {
    try {
      const params = statoFilter !== 'all' ? { stato: statoFilter } : {};
      const response = await preventiviAPI.list(params);
      setPreventivi(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await preventiviAPI.delete(selectedPreventivo.id);
      toast.success('Preventivo eliminato');
      setDeleteDialogOpen(false);
      fetchPreventivi();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const filteredPreventivi = preventivi.filter((preventivo) =>
    preventivo.codice.toLowerCase().includes(searchTerm.toLowerCase()) ||
    preventivo.cliente_ragione_sociale.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="preventivi-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Preventivi</h1>
            <p className="text-slate-500 mt-1">Gestisci i tuoi preventivi</p>
          </div>
          <Link to="/preventivi/nuovo">
            <Button data-testid="new-preventivo-btn" className="bg-brand-orange hover:bg-brand-orange-dark text-white">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Preventivo
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per codice o cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="preventivi-search"
            />
          </div>
          <Select value={statoFilter} onValueChange={setStatoFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="preventivi-stato-filter">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filtra per stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="bozza">Bozza</SelectItem>
              <SelectItem value="inviato">Inviato</SelectItem>
              <SelectItem value="accettato">Accettato</SelectItem>
              <SelectItem value="rifiutato">Rifiutato</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {filteredPreventivi.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Codice</TableHead>
                    <TableHead className="font-semibold">Cliente</TableHead>
                    <TableHead className="font-semibold">Data</TableHead>
                    <TableHead className="font-semibold">Stato</TableHead>
                    <TableHead className="font-semibold text-right">Totale</TableHead>
                    <TableHead className="text-right font-semibold">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPreventivi.map((preventivo) => (
                    <TableRow key={preventivo.id} className="table-row-hover">
                      <TableCell className="font-medium font-mono">
                        {preventivo.codice}
                      </TableCell>
                      <TableCell>{preventivo.cliente_ragione_sociale}</TableCell>
                      <TableCell>{formatDate(preventivo.data)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getStatusColor(preventivo.stato)}>
                          {getStatusLabel(preventivo.stato)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(preventivo.totale_scontato)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Link to={`/preventivi/${preventivo.id}`}>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`view-preventivo-${preventivo.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedPreventivo(preventivo);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-preventivo-${preventivo.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">
                  {searchTerm || statoFilter !== 'all'
                    ? 'Nessun preventivo trovato'
                    : 'Nessun preventivo ancora'}
                </p>
                {!searchTerm && statoFilter === 'all' && (
                  <Link to="/preventivi/nuovo">
                    <Button variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Crea il primo preventivo
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Elimina Preventivo</AlertDialogTitle>
              <AlertDialogDescription>
                Sei sicuro di voler eliminare il preventivo "{selectedPreventivo?.codice}"? Questa azione non può essere annullata.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Elimina
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Preventivi;
